import type { CSSProperties } from 'react'
import { Component, ErrorInfo, ReactNode } from 'react'
import { logger } from '../../lib/logger'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)
    logger.error(
      `Caught: ${error.name}: ${error.message}\nstack: ${error.stack ?? ''}\ncomponentStack: ${errorInfo.componentStack ?? ''}`,
      'ErrorBoundary',
    )
    // Additive: feed the debug-bridge's captured-error buffer via a generic
    // window CustomEvent so the bridge does not need a direct dependency on
    // the ErrorBoundary. See `apps/stream/src/shared/debug/error-buffer.ts`.
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('stream:debug:error', {
            detail: {
              ts: new Date().toISOString(),
              source: 'boundary',
              message: `${error.name}: ${error.message}`,
              stack: error.stack,
            },
          }),
        )
      }
    } catch {
      // Never let a dispatch failure escape the boundary.
    }
    this.setState({ errorInfo })
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--error)"
              strokeWidth="2"
              style={styles.icon}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            {this.state.errorInfo && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error Details</summary>
                <pre style={styles.stack}>{this.state.error?.stack}</pre>
              </details>
            )}
            <button style={styles.button} onClick={this.handleReset}>
              Try Again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  content: {
    display: 'flex',
    maxWidth: '500px',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'center',
  },
  icon: {
    opacity: 0.8,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  message: {
    margin: 0,
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  details: {
    marginTop: '8px',
    width: '100%',
    textAlign: 'left',
  },
  summary: {
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  stack: {
    marginTop: '8px',
    maxHeight: '200px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-tertiary)',
    padding: '12px',
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  button: {
    marginTop: '8px',
    cursor: 'pointer',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--accent-primary)',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'white',
  },
}
